'use client';

import * as React from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { cx } from '../utils';
import { IS_PROD, devWarnDefaultIgnored } from '../internal/devWarn';
import { JIcon } from '../../icons';
import type { UnsafeProps } from '../escape-hatches';

/* Checkbox is controlled-only. Omit `defaultChecked` from the spread
   surface so a consumer who accidentally passes both `checked` and
   `defaultChecked` doesn't trigger React's "controlled/uncontrolled
   input" warning. If you need an uncontrolled checkbox, drop down to
   a native <input type="checkbox"> — styling it to match the kit is
   a single .j-checkbox-box/.j-checkbox-label pair. */
export interface CheckboxProps
  extends
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      'className' | 'style' | 'type' | 'defaultChecked' | 'size'
    >,
    UnsafeProps {
  label?: ReactNode;
  className?: string;
  style?: CSSProperties;
  /** When true, renders a dash icon and sets aria-checked="mixed". */
  indeterminate?: boolean;
  /** Visual size variant. `'sm'` renders a compact 16 px box. @default 'md' */
  size?: 'sm' | 'md';
}

/* The extra `defaultChecked` binding closes the runtime hole when
   consumers cast through unknown. The TS Omit on CheckboxProps
   blocks the typed path; this destructure drains anything that
   slipped through so it never reaches `...rest` and from there
   the native input. The _ prefix suppresses the unused-var lint. */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  {
    checked,
    onChange,
    label,
    disabled,
    indeterminate,
    size = 'md',
    className,
    style,
    unsafe_className,
    unsafe_style,
    defaultChecked: ignoredDefaultChecked,
    ...rest
  }: CheckboxProps & { defaultChecked?: unknown },
  forwardedRef,
) {
  const innerRef = React.useRef<HTMLInputElement>(null);
  const mergedRef = React.useMemo(() => {
    return (node: HTMLInputElement | null) => {
      (innerRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
      }
    };
  }, [forwardedRef]);

  /* Sync the native indeterminate property via ref — there is no HTML
     attribute for indeterminate; it must be set imperatively. */
  React.useEffect(() => {
    if (innerRef.current) {
      innerRef.current.indeterminate = !!indeterminate;
    }
  }, [indeterminate]);

  React.useEffect(() => {
    if (!IS_PROD && ignoredDefaultChecked !== undefined) {
      devWarnDefaultIgnored('Checkbox', 'defaultChecked');
    }
  }, [ignoredDefaultChecked]);

  /* A Checkbox without a label prop AND without aria-label /
     aria-labelledby / aria-describedby is announced by screen
     readers as "checkbox, not checked" — no context. Warn once
     per missing-name instance. Pattern mirrors SegmentedControl /
     Modal ariaLabel warnings. */
  const restProps = rest as {
    'aria-label'?: string;
    'aria-labelledby'?: string;
    'aria-describedby'?: string;
  };
  const hasAccessibleName =
    label != null ||
    restProps['aria-label'] != null ||
    restProps['aria-labelledby'] != null ||
    restProps['aria-describedby'] != null;
  const warnedNameRef = React.useRef(false);
  React.useEffect(() => {
    if (IS_PROD || warnedNameRef.current || hasAccessibleName) return;
    warnedNameRef.current = true;
    // eslint-disable-next-line @atelierpx/jacquard/no-bare-console
    console.warn(
      '[JKit.Checkbox] rendered without an accessible name. Pass ' +
        '`label` (visible text), `aria-label` (invisible), or ' +
        '`aria-labelledby` (points at a label element). Screen ' +
        'readers otherwise announce it as just "checkbox, not checked".',
    );
  }, [hasAccessibleName]);

  // Keep controlled: use false (not undefined) when indeterminate to avoid
  // React's "controlled to uncontrolled" warning.
  const isChecked = indeterminate ? false : !!checked;
  const ariaChecked = indeterminate ? ('mixed' as const) : undefined;
  const dataChecked = indeterminate ? 'mixed' : checked ? 'true' : 'false';

  return (
    <label
      className={cx(
        'j-checkbox-label',
        size === 'sm' && 'j-checkbox-sm',
        className,
        unsafe_className,
      )}
      style={unsafe_style ? { ...style, ...unsafe_style } : style}
    >
      <input
        {...rest}
        ref={mergedRef}
        type="checkbox"
        checked={isChecked}
        aria-checked={ariaChecked}
        disabled={disabled}
        onChange={onChange}
      />
      <span className="j-checkbox-box" data-checked={dataChecked}>
        {indeterminate ? (
          <svg width={12} height={12} viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <line
              x1={2}
              y1={6}
              x2={10}
              y2={6}
              stroke="var(--color-neutral-100)"
              strokeWidth={2.5}
              strokeLinecap="round"
            />
          </svg>
        ) : (
          checked && (
            <JIcon
              intent="check"
              size={12}
              color="var(--color-neutral-100)"
              strokeWidth={3}
              aria-hidden="true"
            />
          )
        )}
      </span>
      {label && <span>{label}</span>}
    </label>
  );
});
